/**
 * AccessMatrixScreen — Tela de Matriz de Acessos (RBAC granular).
 *
 * Portada do protótipo: grade 10 recursos × 5 ações com toggle por célula,
 * lista lateral de matrizes e tabela de associação usuário↔matriz.
 *
 * Ligação com a API (F0-BE-2):
 *   GET  /access-matrix/matrices                      → useMatrices()
 *   GET  /access-matrix/matrices/{id}/permissions     → useMatrixPermissions(id)
 *   PUT  /access-matrix/matrices/{id}/permissions     → useSavePermissions()   [Admin]
 *   GET  /access-matrix/users                         → useAccessUsers()
 *   POST /access-matrix/users/{uid}/matrix            → useAssignUserMatrix()  [Admin]
 *   POST /access-matrix/users                         → useCreateUser()        [Admin]
 *
 * RBAC no front (UX apenas — backend valida em profundidade):
 *   - Só Admin (matrix_id === 'm-admin') vê os controles de edição (toggles + Save).
 *   - Admin edita qualquer matriz, inclusive as de sistema (is_system é só um rótulo
 *     de papel-base; o backend autoriza via access_matrix:administer). Não-Admin: read-only.
 *   - 403 do backend exibe ForbiddenBanner.
 *
 * Acessibilidade:
 *   - Tabela com scope="col"/"row".
 *   - Botões de célula com aria-label e aria-pressed.
 *   - ForbiddenBanner tem role="alert" + aria-live="assertive".
 *   - prefers-reduced-motion: respeita NavaStyles / QueryState.
 */
import { useState, useEffect, useCallback } from 'react';
import {
  ShieldCheck, Layers, Plus, Trash2, Check, Users, ChevronRight, ChevronLeft,
  Save, Loader2, UserPlus,
} from 'lucide-react';
import { C } from '@/theme/palette';
import { Card, Btn, Badge } from '@/components/ui';
import { ForbiddenBanner } from '@/components/ui/ForbiddenBanner';
import { LoadingSpinner, QueryError, MutationError } from '@/components/ui/QueryState';
import {
  useMatrices,
  useMatrixPermissions,
  useAccessUsers,
  useSavePermissions,
  useAssignUserMatrix,
  useCreateUser,
} from '@/api/accessMatrix';
import { CadastroDeUsuarioModal } from './accessMatrix/CadastroDeUsuarioModal';
import { useAuth } from '@/auth/AuthContext';
import { useNavigationContext } from '@/navigation';

// ─── Constantes RBAC (espelho do protótipo / KB 04) ──────────────────────────

// A grade é indexada pelos SLUGS que a API usa (resource × action); os rótulos
// em pt-BR são só apresentação. Tratar o rótulo como chave — como era antes —
// fazia `grid['Dashboard & Projetos']` ser sempre undefined: nenhuma célula
// aparecia marcada e o clique gravava numa chave que o backend não reconhece.
const ACCESS_RESOURCES = [
  { slug: 'dashboard_projects', label: 'Dashboard & Projetos' },
  { slug: 'squads_designer',    label: 'Squads & Designer' },
  { slug: 'quality_gates',      label: 'Quality Gates' },
  { slug: 'agent_catalog',      label: 'Catálogo de Agents' },
  { slug: 'live_board',         label: 'Execuções (Live Board)' },
  { slug: 'budget_costs',       label: 'Budget & Custos' },
  { slug: 'data_sources',       label: 'Data Sources / RAG' },
  { slug: 'settings',           label: 'Configurações' },
  { slug: 'audit_logs',         label: 'Auditoria & Logs' },
  { slug: 'access_matrix',      label: 'Matriz de Acessos' },
];

const ACCESS_ACTIONS = [
  { slug: 'view',         label: 'Visualizar' },
  { slug: 'create_edit',  label: 'Criar/Editar' },
  { slug: 'approve_gate', label: 'Aprovar Gate' },
  { slug: 'execute',      label: 'Executar' },
  { slug: 'administer',   label: 'Administrar' },
];

// ─── Cores de fallback para matrizes (caso o backend não envie `color`) ───────

const MATRIX_COLOR_MAP = {
  'm-admin':      C.hot,
  'm-manager':    C.digital,
  'm-analyze':    C.cyber,
  'm-estimative': C.quantum,
  'm-viewer':     C.neutralMuted,
};

/**
 * Resolve a cor da matriz para um valor CSS real.
 *
 * O backend envia `color_token` como NOME do token ('hot', 'cyber'), não como
 * cor. Usar esse nome direto em backgroundColor é CSS inválido: o navegador
 * ignora a regra e a célula fica sem fundo — foi o que apagava o estado
 * "marcado" da grade inteira. Resolvemos o token contra a paleta e só então
 * caímos no mapa por slug (não por id, que é UUID e nunca casava).
 */
function matrixColor(matrix) {
  const token = matrix.color ?? matrix.color_token;
  if (token && C[token]) return C[token];        // 'hot' → C.hot
  if (token && String(token).startsWith('#')) return token;  // já é cor
  return MATRIX_COLOR_MAP[matrix.slug] || C.neon;
}

// ─── Estilos de tabela (idênticos ao protótipo) ───────────────────────────────

const thStyle = {
  textAlign: 'left',
  padding: '12px 16px',
  fontSize: 11,
  fontWeight: 600,
  color: C.neutralMuted,
  letterSpacing: '0.06em',
  textTransform: 'uppercase',
};

const tdStyle = { padding: '14px 16px', fontSize: 14 };

// ─── Sub-componente: lista lateral de matrizes ────────────────────────────────

function MatrixList({ matrices, selectedId, onSelect, onAdd, isAdmin }) {
  return (
    <Card padded={false}>
      <div
        className="flex items-center justify-between"
        style={{ padding: '16px 16px 12px' }}
      >
        <div className="flex items-center gap-2">
          <Layers size={15} style={{ color: C.cyber }} aria-hidden="true" />
          <h3 style={{ fontSize: 14, fontWeight: 600, color: C.neutral }}>Matrizes</h3>
        </div>
        {isAdmin && (
          <button
            onClick={onAdd}
            aria-label="Criar nova matriz"
            style={{
              color: C.cyber,
              display: 'flex',
              alignItems: 'center',
              gap: 4,
              fontSize: 12,
              fontWeight: 500,
              background: 'transparent',
              border: 'none',
              cursor: 'pointer',
            }}
          >
            <Plus size={14} aria-hidden="true" /> Nova
          </button>
        )}
      </div>

      <div style={{ padding: '0 8px 8px' }}>
        {matrices.map((m) => {
          const active = m.id === selectedId;
          const color  = matrixColor(m);
          return (
            <button
              key={m.id}
              onClick={() => onSelect(m.id)}
              aria-pressed={active}
              aria-label={`Selecionar matriz ${m.name}`}
              style={{
                width: '100%',
                textAlign: 'left',
                padding: '10px 12px',
                borderRadius: 6,
                marginBottom: 4,
                border: active ? `1px solid ${C.cyber}` : '1px solid transparent',
                backgroundColor: active ? `${C.cyber}11` : 'transparent',
                cursor: 'pointer',
              }}
            >
              <div className="flex items-center gap-2">
                <span
                  style={{
                    width: 8,
                    height: 8,
                    borderRadius: '50%',
                    backgroundColor: color,
                    flexShrink: 0,
                  }}
                  aria-hidden="true"
                />
                <span style={{ fontSize: 13, color: C.neutral, fontWeight: 600 }}>
                  {m.name}
                </span>
                {m.is_system && (
                  <Badge color={C.neutralMuted} light>SISTEMA</Badge>
                )}
              </div>
              <div style={{ fontSize: 11, color: C.neutralDim, marginTop: 4 }}>
                {m.permission_count ?? '—'} {m.permission_count === 1 ? 'permissão' : 'permissões'}
                {' · '}
                {m.user_count ?? '—'} {m.user_count === 1 ? 'usuário' : 'usuários'}
              </div>
            </button>
          );
        })}
      </div>
    </Card>
  );
}

// ─── Sub-componente: grade de permissões ──────────────────────────────────────

function PermissionGrid({
  matrix,
  grid,
  onToggle,
  isAdmin,
  isSaving,
  saveError,
  onSave,
  onDismissError,
  isDirty,
}) {
  // Decisão de produto: o Administrador edita QUALQUER matriz, inclusive as de
  // sistema (a imutabilidade de is_system foi removida no back e no front). Só
  // não-Admin fica read-only. O backend reforça via access_matrix:administer.
  const readOnly = !isAdmin;
  const color    = matrixColor(matrix);

  return (
    <Card>
      {/* Cabeçalho da grade */}
      <div className="flex items-start justify-between mb-1">
        <div className="flex items-center gap-2">
          <span
            style={{ width: 10, height: 10, borderRadius: '50%', backgroundColor: color }}
            aria-hidden="true"
          />
          <h3 style={{ fontSize: 16, fontWeight: 600, color: C.neutral }}>
            {matrix.name}
          </h3>
          {readOnly
            ? <Badge color={C.neutralMuted} light>SOMENTE LEITURA</Badge>
            : <Badge color={C.cyber} light>EDITÁVEL</Badge>}
        </div>

        {/* Botão Salvar — visível apenas para Admin em matrizes editáveis */}
        {!readOnly && isDirty && (
          <Btn
            variant="primary"
            size="sm"
            icon={isSaving ? Loader2 : Save}
            disabled={isSaving}
            onClick={onSave}
            aria-label="Salvar alterações de permissões"
          >
            {isSaving ? 'Salvando...' : 'Salvar'}
          </Btn>
        )}
      </div>

      <p style={{ fontSize: 13, color: C.neutralDim, marginBottom: 16 }}>
        {matrix.description}
      </p>

      {/* Banner de erro de mutation */}
      {saveError && (
        <MutationError error={saveError} onDismiss={onDismissError} />
      )}

      {/* Grade 10×5 */}
      <div style={{ overflowX: 'auto' }}>
        <table
          style={{ width: '100%', borderCollapse: 'collapse' }}
          aria-label={`Permissões da matriz ${matrix.name}`}
        >
          <thead>
            <tr style={{ borderBottom: `1px solid ${C.blackBorder}` }}>
              <th scope="col" style={{ ...thStyle, paddingLeft: 4 }}>Recurso</th>
              {ACCESS_ACTIONS.map((a) => (
                <th
                  key={a.slug}
                  scope="col"
                  style={{ ...thStyle, textAlign: 'center', whiteSpace: 'nowrap' }}
                >
                  {a.label}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {ACCESS_RESOURCES.map((r, idx) => (
              <tr
                key={r.slug}
                style={{
                  borderBottom: `1px solid ${C.blackBorder}`,
                  // Zebra sutil: em 10 linhas × 5 colunas de caixinhas iguais,
                  // é o que evita o olho pular de linha ao conferir uma célula.
                  backgroundColor: idx % 2 ? 'transparent' : `${C.blackElev}55`,
                }}
              >
                <th
                  scope="row"
                  style={{
                    padding: '10px 4px',
                    fontSize: 13,
                    color: C.neutral,
                    fontWeight: 400,
                    textAlign: 'left',
                  }}
                >
                  {r.label}
                </th>
                {ACCESS_ACTIONS.map((a) => {
                  // Dados pelo slug; rótulo só na apresentação/acessibilidade.
                  const on = grid?.[r.slug]?.[a.slug] ?? false;
                  return (
                    <td key={a.slug} style={{ textAlign: 'center', padding: '8px 0' }}>
                      <button
                        onClick={() => !readOnly && onToggle(r.slug, a.slug)}
                        disabled={readOnly}
                        aria-label={`${r.label} · ${a.label} — ${on ? 'permitido' : 'negado'}`}
                        aria-pressed={on}
                        title={`${r.label} · ${a.label}`}
                        style={{
                          width: 26,
                          height: 26,
                          borderRadius: 6,
                          display: 'inline-flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          // Estado marcado: fundo sólido na cor da matriz. Negado:
                          // caixa vazia com borda discreta — a diferença precisa
                          // ser óbvia de relance, é o dado central da tela.
                          border: on
                            ? `1px solid ${color}`
                            : `1px solid ${C.blackBorder}`,
                          backgroundColor: on ? color : 'transparent',
                          cursor: readOnly ? 'not-allowed' : 'pointer',
                          opacity: readOnly ? 0.55 : 1,
                          transition: 'background-color 120ms, border-color 120ms, transform 80ms',
                          padding: 0,
                        }}
                        onMouseEnter={(e) => {
                          if (readOnly) return;
                          e.currentTarget.style.borderColor = color;
                          e.currentTarget.style.transform = 'scale(1.12)';
                        }}
                        onMouseLeave={(e) => {
                          if (readOnly) return;
                          e.currentTarget.style.borderColor = on ? color : C.blackBorder;
                          e.currentTarget.style.transform = 'scale(1)';
                        }}
                      >
                        {on && (
                          <Check
                            size={15}
                            strokeWidth={3}
                            style={{ color: C.black }}
                            aria-hidden="true"
                          />
                        )}
                      </button>
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <p style={{ fontSize: 12, color: C.neutralMuted, marginTop: 12 }}>
        {readOnly
          ? 'Somente Administradores podem editar permissões.'
          : matrix.is_system
            ? 'Matriz de sistema (papel-base). Como Admin, você pode ajustá-la — clique nas células e salve.'
            : 'Clique nas células para conceder ou revogar permissões. Salve para persistir.'}
      </p>
    </Card>
  );
}

// ─── Sub-componente: tabela de usuários ──────────────────────────────────────

function UsersTable({ users, matrices, isAdmin, onAssign, assigningUserId }) {
  if (!users?.length) {
    return (
      <div style={{ padding: '24px', textAlign: 'center', color: C.neutralDim, fontSize: 13 }}>
        Nenhum usuário encontrado.
      </div>
    );
  }

  return (
    <table
      style={{ width: '100%', borderCollapse: 'collapse' }}
      aria-label="Usuários e matrizes associadas"
    >
      <thead>
        <tr style={{ borderBottom: `1px solid ${C.blackBorder}` }}>
          <th scope="col" style={thStyle}>Usuário</th>
          <th scope="col" style={thStyle}>Papel</th>
          <th scope="col" style={thStyle}>Matriz de acesso</th>
        </tr>
      </thead>
      <tbody>
        {users.map((u) => {
          const m          = matrices?.find((x) => x.id === u.matrix_id);
          const mColor     = m ? matrixColor(m) : C.neutralMuted;
          const isAssigning = assigningUserId === u.id;

          return (
            <tr key={u.id} style={{ borderBottom: `1px solid ${C.blackBorder}` }}>
              {/* Usuário */}
              <td style={tdStyle}>
                <div className="flex items-center gap-3">
                  <div
                    aria-hidden="true"
                    style={{
                      width: 30,
                      height: 30,
                      borderRadius: '50%',
                      backgroundColor: C.blackElev2,
                      color: C.cyber,
                      fontSize: 11,
                      fontWeight: 600,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      flexShrink: 0,
                    }}
                  >
                    {u.name.split(' ').map((p) => p[0]).slice(0, 2).join('')}
                  </div>
                  <div>
                    <div style={{ color: C.neutral, fontWeight: 500 }}>{u.name}</div>
                    <div style={{ color: C.neutralMuted, fontSize: 12 }}>{u.email}</div>
                  </div>
                </div>
              </td>

              {/* Papel */}
              <td style={{ ...tdStyle, color: C.neutralDim, fontSize: 13 }}>{u.role}</td>

              {/* Seletor de matriz */}
              <td style={tdStyle}>
                <div className="flex items-center gap-2">
                  <span
                    aria-hidden="true"
                    style={{
                      width: 8,
                      height: 8,
                      borderRadius: '50%',
                      backgroundColor: mColor,
                      flexShrink: 0,
                    }}
                  />
                  {isAdmin ? (
                    <div style={{ position: 'relative' }}>
                      {isAssigning && (
                        <Loader2
                          size={12}
                          aria-hidden="true"
                          style={{
                            color: C.cyber,
                            position: 'absolute',
                            right: 10,
                            top: '50%',
                            transform: 'translateY(-50%)',
                            animation: 'nava-spin 0.8s linear infinite',
                          }}
                        />
                      )}
                      <select
                        value={u.matrix_id ?? ''}
                        onChange={(e) => onAssign(u.id, e.target.value)}
                        disabled={isAssigning}
                        aria-label={`Matriz de acesso de ${u.name}`}
                        style={{
                          appearance: 'none',
                          backgroundColor: C.black,
                          border: `1px solid ${C.blackBorder}`,
                          color: C.neutral,
                          padding: '7px 30px 7px 10px',
                          borderRadius: 6,
                          fontSize: 13,
                          outline: 'none',
                          cursor: isAssigning ? 'not-allowed' : 'pointer',
                          opacity: isAssigning ? 0.6 : 1,
                        }}
                      >
                        {(matrices ?? []).map((mx) => (
                          <option key={mx.id} value={mx.id}>{mx.name}</option>
                        ))}
                      </select>
                      {!isAssigning && (
                        <ChevronRight
                          size={13}
                          aria-hidden="true"
                          style={{
                            position: 'absolute',
                            right: 10,
                            top: '50%',
                            transform: 'translateY(-50%) rotate(90deg)',
                            color: C.neutralDim,
                            pointerEvents: 'none',
                          }}
                        />
                      )}
                    </div>
                  ) : (
                    <span style={{ fontSize: 13, color: C.neutral }}>{m?.name ?? u.matrix_id}</span>
                  )}
                </div>
              </td>
            </tr>
          );
        })}
      </tbody>
    </table>
  );
}

// ─── Tela principal ───────────────────────────────────────────────────────────

export function AccessMatrixScreen() {
  const { navigate }      = useNavigationContext();
  const { user }          = useAuth();

  // Matriz selecionada
  const [selectedId, setSelectedId] = useState(null);

  // Grade local (cópia editável, sincroniza da query)
  const [localGrid, setLocalGrid]   = useState(null);
  const [isDirty, setIsDirty]       = useState(false);
  const [saveError, setSaveError]   = useState(null);

  // Usuário sendo reatribuído (para feedback de loading individual)
  const [assigningUserId, setAssigningUserId] = useState(null);

  // Cadastro de usuário (popup) e o aviso de quem acabou de ser cadastrado
  const [cadastroAberto, setCadastroAberto] = useState(false);
  const [cadastrado, setCadastrado] = useState(null);

  // ── Queries ──────────────────────────────────────────────────────────────────
  const matricesQuery    = useMatrices();
  const permissionsQuery = useMatrixPermissions(selectedId);
  const usersQuery       = useAccessUsers();

  // isAdmin: o claim matrix_id do JWT pode vir como slug ('m-admin', dev/Fase 0)
  // OU como UUID (produção/seed). Aceita ambos, resolvendo o UUID da matriz
  // 'm-admin' a partir da lista carregada.
  const adminMatrixId = (matricesQuery.data ?? []).find((m) => m.slug === 'm-admin')?.id;
  const isAdmin = user?.matrix_id === 'm-admin'
    || (!!adminMatrixId && user?.matrix_id === adminMatrixId);

  // ── Mutations ────────────────────────────────────────────────────────────────
  const savePermMutation = useSavePermissions();
  const assignMutation   = useAssignUserMatrix();
  const createMutation   = useCreateUser();

  const abrirCadastro = () => { createMutation.reset(); setCadastrado(null); setCadastroAberto(true); };
  const cadastrar = (dados) => createMutation.mutate(dados, {
    onSuccess: () => { setCadastroAberto(false); setCadastrado(dados); },
  });

  // Inicializa selectedId com a primeira matriz quando a lista chegar
  useEffect(() => {
    if (!selectedId && matricesQuery.data?.length) {
      setSelectedId(matricesQuery.data[0].id);
    }
  }, [matricesQuery.data, selectedId]);

  // Sincroniza localGrid quando a query de permissões retorna
  useEffect(() => {
    if (permissionsQuery.data) {
      setLocalGrid(permissionsQuery.data);
      setIsDirty(false);
      setSaveError(null);
    }
  }, [permissionsQuery.data]);

  // Reseta grid ao trocar de matriz
  const handleSelectMatrix = useCallback((id) => {
    setSelectedId(id);
    setLocalGrid(null);
    setIsDirty(false);
    setSaveError(null);
  }, []);

  // Toggle de célula
  const handleToggle = useCallback((resource, action) => {
    setLocalGrid((prev) => {
      if (!prev) return prev;
      return {
        ...prev,
        [resource]: {
          ...prev[resource],
          [action]: !prev[resource]?.[action],
        },
      };
    });
    setIsDirty(true);
    setSaveError(null);
  }, []);

  // Salvar permissões
  const handleSave = useCallback(() => {
    if (!selectedId || !localGrid) return;
    setSaveError(null);
    savePermMutation.mutate(
      { matrixId: selectedId, grid: localGrid },
      {
        onSuccess: () => setIsDirty(false),
        onError: (err) => setSaveError(err),
      },
    );
  }, [selectedId, localGrid, savePermMutation]);

  // Reatribuir usuário
  const handleAssign = useCallback((userId, matrixId) => {
    setAssigningUserId(userId);
    assignMutation.mutate(
      { userId, matrixId },
      {
        onSettled: () => setAssigningUserId(null),
      },
    );
  }, [assignMutation]);

  const matrices = matricesQuery.data ?? [];
  const selected = matrices.find((m) => m.id === selectedId);

  // ── Render: loading inicial ──────────────────────────────────────────────────
  if (matricesQuery.isLoading) {
    return (
      <div style={{ padding: 28 }}>
        <LoadingSpinner label="Carregando matrizes de acesso..." />
      </div>
    );
  }

  // ── Render: erro 403 na listagem (sem permissão access_matrix:view) ──────────
  if (matricesQuery.error?.isForbidden) {
    return (
      <div style={{ padding: 28 }}>
        <ForbiddenBanner detail={matricesQuery.error.detail} />
      </div>
    );
  }

  // ── Render: outros erros na listagem ─────────────────────────────────────────
  if (matricesQuery.error) {
    return (
      <div style={{ padding: 28 }}>
        <QueryError
          error={matricesQuery.error}
          onRetry={() => matricesQuery.refetch()}
        />
      </div>
    );
  }

  return (
    <div style={{ padding: 28, maxWidth: 1240, margin: '0 auto' }}>
      {/* Cabeçalho */}
      <div className="flex items-center gap-3 mb-1">
        <button
          onClick={() => navigate('settings')}
          aria-label="Voltar para Configurações"
          style={{
            color: C.neutralDim,
            background: 'transparent',
            border: 'none',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
          }}
        >
          <ChevronLeft size={20} aria-hidden="true" />
        </button>
        <ShieldCheck size={20} style={{ color: C.cyber }} aria-hidden="true" />
        <h1
          style={{
            fontSize: 24,
            fontWeight: 700,
            color: C.neutral,
            letterSpacing: '-0.02em',
            margin: 0,
          }}
        >
          Matriz de Acessos
        </h1>
        <Badge color={C.cyber}>{matrices.length} MATRIZES</Badge>
      </div>
      <p style={{ fontSize: 13, color: C.neutralDim, marginBottom: 24, paddingLeft: 32 }}>
        Defina perfis de permissão por recurso e ação (RBAC granular) e associe cada usuário a uma matriz.
      </p>

      {/* Grid: lista lateral + grade de permissões */}
      <div className="grid gap-4" style={{ gridTemplateColumns: '300px 1fr', alignItems: 'start' }}>
        <MatrixList
          matrices={matrices}
          selectedId={selectedId}
          onSelect={handleSelectMatrix}
          onAdd={() => {/* criação de nova matriz: endpoint POST ainda pendente */}}
          isAdmin={isAdmin}
        />

        {/* Grade de permissões */}
        {permissionsQuery.isLoading || !localGrid ? (
          <Card>
            <LoadingSpinner label="Carregando permissões..." />
          </Card>
        ) : permissionsQuery.error ? (
          <Card>
            <QueryError
              error={permissionsQuery.error}
              onRetry={() => permissionsQuery.refetch()}
            />
          </Card>
        ) : selected ? (
          <PermissionGrid
            matrix={selected}
            grid={localGrid}
            onToggle={handleToggle}
            isAdmin={isAdmin}
            isSaving={savePermMutation.isPending}
            saveError={saveError}
            onSave={handleSave}
            onDismissError={() => setSaveError(null)}
            isDirty={isDirty}
          />
        ) : null}
      </div>

      {/* Tabela de usuários */}
      <Card padded={false} style={{ marginTop: 16 }}>
        <div
          className="flex items-center justify-between"
          style={{ padding: '16px 20px 12px' }}
        >
          <div className="flex items-center gap-2">
            <Users size={16} style={{ color: C.cyber }} aria-hidden="true" />
            <h3 style={{ fontSize: 15, fontWeight: 600, color: C.neutral }}>
              Usuários &amp; matriz associada
            </h3>
          </div>
          <div className="flex items-center gap-3" style={{ flexWrap: 'wrap', justifyContent: 'flex-end' }}>
            {usersQuery.isSuccess && (
              <span style={{ fontSize: 12, color: C.neutralDim }}>
                {usersQuery.data?.length ?? 0} usuários
              </span>
            )}
            {isAdmin && (
              <Btn variant="primary" size="sm" icon={UserPlus} onClick={abrirCadastro}>
                Cadastrar usuário
              </Btn>
            )}
          </div>
        </div>

        {cadastrado && (
          <div role="status" style={{
            margin: '0 20px 12px', padding: '10px 14px', borderRadius: 10, fontSize: 13,
            color: C.neutral, background: `${C.cyber}14`, border: `1px solid ${C.cyber}44`,
          }}>
            <strong>{cadastrado.displayName}</strong> foi cadastrado(a). O acesso é por
            {' '}“Entrar com Microsoft” com o e-mail {cadastrado.email}.
          </div>
        )}

        {usersQuery.isLoading ? (
          <LoadingSpinner label="Carregando usuários..." />
        ) : usersQuery.error ? (
          <div style={{ padding: '0 20px 16px' }}>
            <QueryError
              error={usersQuery.error}
              onRetry={() => usersQuery.refetch()}
            />
          </div>
        ) : (
          <UsersTable
            users={usersQuery.data ?? []}
            matrices={matrices}
            isAdmin={isAdmin}
            onAssign={handleAssign}
            assigningUserId={assigningUserId}
          />
        )}

        <CadastroDeUsuarioModal
          open={cadastroAberto}
          matrices={matrices}
          salvando={createMutation.isPending}
          erro={createMutation.error}
          onSalvar={cadastrar}
          onFechar={() => setCadastroAberto(false)}
        />

        {/* Feedback de erro de reatribuição */}
        {assignMutation.isError && (
          <div style={{ padding: '0 20px 16px' }}>
            <MutationError error={assignMutation.error} />
          </div>
        )}
      </Card>
    </div>
  );
}
